import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: { message?: string } }));
        throw new Error(body.error?.message ?? 'Reset failed');
      }
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base px-4 py-8 sm:px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-6 sm:mb-8">
          <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center text-accent-fg font-bold text-xl">1F</div>
          <h1 className="text-xl font-semibold text-white">Reset password</h1>
        </div>

        <div className="bg-bg-panel border border-border rounded-xl p-5 sm:p-6 space-y-4">
          {done ? (
            <div className="text-center space-y-4">
              <div className="text-emerald-300 text-sm">Your password has been reset.</div>
              <Link to="/login" className="inline-block bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-4 py-2 rounded-lg">
                Sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-muted mb-1.5">New password</label>
                <div className="relative">
                  <input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm text-white placeholder-muted focus:outline-none focus:border-accent pr-14"
                    minLength={12}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-white text-xs"
                    tabIndex={-1}
                  >
                    {show ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-muted mb-1.5">Confirm new password</label>
                <input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
                  required
                />
              </div>

              {error && (
                <div className="text-rose-400 text-sm bg-rose-950 border border-rose-900 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg font-medium text-sm sm:text-base py-3 sm:py-2.5 min-h-[44px] rounded-lg transition-colors"
              >
                {submitting ? 'Saving…' : 'Reset password'}
              </button>

              <div className="text-center text-xs text-muted">
                <Link to="/login" className="text-accent hover:underline">Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
